import { useEffect, useRef } from 'react';

import { useRouter } from 'expo-router';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import {
  ProductWizardStep,
  WizardRouteRedirectTarget,
} from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { InteractionManager } from 'react-native';

import { routes } from 'utils/routing';

/**
 * Applies wizard route redirects for invalid URL params.
 *
 * @returns `true` when the screen must not render until the redirect completes.
 */
export function useProductDetailRouteRedirect(): boolean {
  const router = useRouter();
  const { routeRedirect } = useProductWizardFlow();
  const { hasInAppNavigationRef } = useProductWizardContext();
  const redirectTargetRef = useRef<WizardRouteRedirectTarget | null>(null);

  useEffect(() => {
    if (!routeRedirect) {
      redirectTargetRef.current = null;
      return;
    }

    if (redirectTargetRef.current === routeRedirect) {
      return;
    }

    redirectTargetRef.current = routeRedirect;

    const task = InteractionManager.runAfterInteractions(() => {
      requestAnimationFrame(() => {
        if (routeRedirect === WizardRouteRedirectTarget.Home) {
          // Keep the current modal route and only update query params. router.replace
          // remounts the screen on web and briefly shows two CardModals.
          hasInAppNavigationRef.current = true;
          router.setParams({
            step: ProductWizardStep.ProductHome,
            slotKey: '',
            pickerAspectKey: '',
          });
        } else if (routeRedirect === WizardRouteRedirectTarget.Cart) {
          router.replace(routes.cart);
        } else if (routeRedirect === WizardRouteRedirectTarget.Menu) {
          router.replace(routes.menu);
        }
      });
    });

    return () => task.cancel();
  }, [routeRedirect, router, hasInAppNavigationRef]);

  return routeRedirect !== null;
}
